/***
 * Created on 2024/3/13
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/

import React, {useEffect, useState} from 'react';
import ModalComponent from "../common/ModalComonent/ModalComponent";
import {useTranslation} from "react-i18next";
import useToggle from "../../hook/ToggleHook";
import DeviceAdminContactItemComponent from "./DeviceAdminContactItemComponent";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useOrganizationApi} from "../../hook/OrganizationAPIHook";
import {useParams} from "react-router-dom";
import {useOrganization} from "../../context/OrganizationContext";
import {invalidateAccessGrant} from "../../service/accessGrantSync";

export const filterEligibleDeviceUsers = (users, searchText) => {
    const needle = (searchText || '').toLowerCase();
    return (users || [])
        .filter(u => !(u?.is_owner || u?.role === 'owner'))
        .filter(u => {
            if (!needle) return true;
            const name = (u?.display_name || '').toLowerCase();
            const email = (u?.email || '').toLowerCase();
            return name.includes(needle) || email.includes(needle);
        });
};

export const computeDeviceAdminDelta = (initial, current) => {
    const toIdSet = (list, idField) => new Set(
        (list || []).map(entry => typeof entry === 'object' ? (entry[idField] || entry.id) : entry)
    );
    const initialUsers = toIdSet(initial?.organization_users, 'id');
    const currentUsers = toIdSet(current?.organization_users, 'id');
    const initialGroups = toIdSet(initial?.organization_groups, 'organization_group_id');
    const currentGroups = toIdSet(current?.organization_groups, 'organization_group_id');
    return {
        organization_users: {
            add: [...currentUsers].filter(id => !initialUsers.has(id)),
            remove: [...initialUsers].filter(id => !currentUsers.has(id)),
        },
        organization_groups: {
            add: [...currentGroups].filter(id => !initialGroups.has(id)),
            remove: [...initialGroups].filter(id => !currentGroups.has(id)),
        },
    };
};

function DeviceAddAdminModal({openModal, closeModal, device_id, adminList, dispatch, state}) {
    const { t } = useTranslation();
    const [open, node, context] = useToggle();
    const {oid} = useParams();
    const {OrganizationAPI} = useOrganizationApi();
    const {getCurrentOrg} = useOrganization();
    const currentOrg = getCurrentOrg();
    const [adminType, setAdminType] = useState('qid');
    const [searchQuery, setSearchQuery] = useState("");
    const [deviceId, setDeviceId] = useState(device_id);
    const [localAdminList, setLocalAdminList] = useState({
        organization_users: [...(adminList?.organization_users || [])],
        organization_groups: [...(adminList?.organization_groups || [])],
    });
    const queryClient = useQueryClient();

    const updateAdminMutate = useMutation({
        mutationFn: async (data) => await OrganizationAPI.post(`organization/${oid}/device/${deviceId}/admin`, {}, data),
        onSuccess: (data) => {
            invalidateAccessGrant(queryClient, oid);
            queryClient.invalidateQueries({
                queryKey: [`device_whitelist_user`, deviceId, `/v1.2/device`]
            });
            queryClient.invalidateQueries({
                queryKey: [`/v1.2/device`]
            });
            closeModal();
        },
        onError: (error, variables, context) => {
            console.log(error);
        }
    });

    const onSave = async () => {
        const payload = computeDeviceAdminDelta(adminList, localAdminList);
        dispatch({
            type: 'toggle_contact_admin',
            payload: {
                organization_users: localAdminList.organization_users,
                organization_groups: localAdminList.organization_groups,
            }
        });
        await updateAdminMutate.mutate(payload);
    }

    useEffect(() => {
        queryClient.invalidateQueries({
            queryKey: [`device_whitelist_user`, deviceId]
        });
    }, []);

    return (
        <ModalComponent isOpen={openModal} onClose={closeModal} onSubmit={onSave} title={t('@Add Member as Device Admin@')} okText={t('@Save@')}>
            <div className="dialog-content">
                <div className="comp-contacts">
                    <div className="comp-select mb-16">
                        <div className="select-toggle" ref={node}>
                            <span className="select-label">
                                {adminType === 'qid' ? "QID" : t('@Group@')}
                            </span>
                        </div>
                        <div className={`comp-dropdown comp-select-dropdown ${open==='show' ? 'active' : 'hide'}`}>
                            <ul className="comp-dropdown-menu">
                                <li className="section">
                                    <div className="section-items selection" onClick={()=>setAdminType('qid')}>QID</div>
                                    <div className="section-items selection" onClick={()=>setAdminType('group')}>{t('@Group@')}</div>
                                </li>
                            </ul>
                        </div>
                    </div>
                    <div className="filter-list-group">
                        <div className="filter">
                            <div className="filter-bar">
                                <input type="text" placeholder={t('@Search@')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
                            </div>
                        </div>
                        <ul className="list overlay-scroll">
                            {
                                adminType === 'qid' && filterEligibleDeviceUsers(currentOrg?.users || [], searchQuery).map(user => (
                                    <DeviceAdminContactItemComponent
                                        contactType={adminType}
                                        contact={user}
                                        admin={localAdminList}
                                        onToggle={setLocalAdminList}
                                        key={user.id}
                                        state={state}
                                    />
                                ))
                            }
                            {
                                adminType === 'group' && currentOrg?.groups?.filter(group => group.name.toLowerCase().includes(searchQuery.toLowerCase())).map(group => <DeviceAdminContactItemComponent contactType={adminType} contact={group} admin={localAdminList} onToggle={setLocalAdminList} key={group.organization_group_id} state={state}/>)
                            }
                        </ul>
                    </div>
                </div>
            </div>
        </ModalComponent>
    );
}

export default DeviceAddAdminModal;